import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArtistDTO, Song } from "../../spotipitan/types";
import { getSongAudio, removeSongFromAlbum, Spotip17anFunctions } from "../../../../utils/api";
import ArtistHeader from "../../../shared/artist-header/artist-header";
import { SongList } from "../../spotipitan/song-list";
import useSongStore from "../../spotipitan/store/SongState";
import { DiscordBotApi } from "../../../../utils/discordBotApi";
import SeparatorLine from "../../../shared/separator-line/separator-line";
import useUserStore from "../../../../store/UserState";
import { LoginHome } from "../../../users";
import { getPlaylistFromDB, getSongFromDB, savePlaylistToDB, saveSongToDB } from "../../../../utils/dbUtils";
import { AlbumEditModal } from "../../../shared/album-edit-modal";
import toast from "react-hot-toast";
import { useNavigateToArtist } from "../../../../hooks";
import './spotip17an-playlist.scss';

interface Props {
    isAllSongs?: boolean;
}

const Spotip17anPlaylist = ({ isAllSongs = false }: Props) => {
    const { id } = useParams();
    const [artist, setArtist] = useState<ArtistDTO>();
    const [loading, setLoading] = useState(true);
    const loadStatus = useRef<"none" | "loading" | "loaded">("none");
    const navigate = useNavigate();
    const songStore = useSongStore(state => state);
    const { user, spotip17anUser, offlineMode } = useUserStore(state => state.state);
    const [needAuth, setNeedAuth] = useState(false);
    const [owner, setOwner] = useState(false);
    const [saving, setSaving] = useState(false);
    const [songs, setSongs] = useState<Song[]>([]);
    const [showEditAlbumModal, setShowEditAlbumModal] = useState(false);
    const loadedId = useRef<string>("");
    const [statusError, setStatusError] = useState<number>(0);
    const contentRef = useRef<HTMLDivElement>(null);
    const [showSecondaryHeader, setShowSecondaryHeader] = useState(false);

    const targetPlaylistId = isAllSongs ? "all-songs" : id;

    // Detect if this playlist is actually an Album
    const isAlbum = useMemo(() => {
        if (!artist) return false;
        if (artist.id?.toString().includes("favs")) return false;
        const pid = String(targetPlaylistId || "");
        if (pid.startsWith("album-") || pid.startsWith("sp-album-") || pid.startsWith("MPREb_") || pid.startsWith("dz-album-") || pid.startsWith("OLAK5uy_")) {
            return true;
        }
        if (artist.type === 'album') return true;
        if (artist.is_native && artist.spotip17an_artist) return true;
        // If it has a creator_name (artist name), no creator_uid (not user-made) and not a native user
        if (artist.creator_name && !artist.creator_uid && !artist.spotip17an_artist) {
            return true;
        }
        return false;
    }, [artist, targetPlaylistId]);

    const albumCreatorName = useMemo(() => {
        if (!artist) return "";
        if (artist.creator_name) return artist.creator_name;
        if (isAlbum) {
            return artist.songs?.[0]?.user?.username || "";
        }
        return "";
    }, [artist, isAlbum]);

    const { handleNavigateToArtist } = useNavigateToArtist({
        artist,
        fallbackName: albumCreatorName,
    });

    useEffect(() => {
        if (offlineMode || !targetPlaylistId || !user?.uid) {
            return;
        }

        Spotip17anFunctions.use('addToPlaylistHistory').post({ playlistId: targetPlaylistId }).catch(() => {
            console.error("Error adding to playlist history");
        });
    }, [targetPlaylistId, offlineMode, user?.uid]);

    useEffect(() => {
        if (loadedId.current === targetPlaylistId) {
            return;
        }

        if (!targetPlaylistId) {
            navigate("/");
            return;
        }

        const pid = String(targetPlaylistId);
        const isArtistId = pid.startsWith("sp-artist-") ||
            pid.startsWith("dz-artist-") ||
            pid.startsWith("UC") ||
            (!isNaN(Number(pid)) && pid.length < 5 && Number(pid) > 0);

        if (isArtistId) {
            navigate(`/artist/${pid}`, { replace: true });
            return;
        }

        if (loadStatus.current !== "none") {
            return;
        }
        loadStatus.current = "loading";

        if (offlineMode) {
            getPlaylistFromDB(targetPlaylistId).then((res: ArtistDTO | null) => {
                if (res) {
                    setArtist(res);
                    setSongs(res.songs ?? []);
                    setLoading(false);
                    setNeedAuth(false);
                    loadedId.current = targetPlaylistId;
                    loadStatus.current = "loaded";
                }
            }).catch(() => {
                setLoading(false);
            });
            return;
        }

        Spotip17anFunctions.use('fetchPlaylist').call({ playlistId: targetPlaylistId }).then((res: ArtistDTO) => {
            if (res?.type === 'artist') {
                navigate(`/artist/${targetPlaylistId}`, { replace: true });
                return;
            }
            const isCurrentOwner = !!user?.uid && (res.creator_uid === user.uid || (!!spotip17anUser && (res as any).artist_id === spotip17anUser.id));
            setOwner(isCurrentOwner);
            setArtist(res);
            setSongs(res?.songs ?? []);
            setLoading(false);
            setNeedAuth(false);
            loadStatus.current = "loaded";
            loadedId.current = targetPlaylistId;
            setStatusError(0);
        }).catch((e) => {
            setLoading(false);
            setStatusError(e.code || 500);
            setNeedAuth(e.code === 401 && !user?.uid);
            loadStatus.current = "none";
            console.log("Error fetching playlist", e);
        });
    }, [user, loading, targetPlaylistId, navigate, offlineMode]);

    useEffect(() => {
        if (targetPlaylistId !== artist?.id) {
            setArtist(undefined);
            loadStatus.current = "none";
            setLoading(true);
        }
    }, [targetPlaylistId]);

    useEffect(() => {
        if (artist && user?.uid) {
            setOwner(artist.creator_uid === user.uid || (!!spotip17anUser && (artist as any).artist_id === spotip17anUser.id));
        }
    }, [artist, user, spotip17anUser]);

    useEffect(() => {
        const container = contentRef.current;
        const handleScroll = () => {
            if ((container?.scrollTop || 0) > 180) {
                setShowSecondaryHeader(true);
            } else {
                setShowSecondaryHeader(false);
            }
        };

        container?.addEventListener("scroll", handleScroll);
        return () => {
            container?.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const handleClickPlay = (songsToPlay: Song[]) => {
        if (songStore.state.isBotMode && songStore.state.botGuildId && targetPlaylistId) {
            (async () => {
                try {
                    await DiscordBotApi.stop(songStore.state.botGuildId!);
                } catch (e) {
                    console.warn("Could not stop bot before playing playlist:", e);
                }
                DiscordBotApi.play(
                    songStore.state.botGuildId!,
                    `https://spotip17an.es/playlist/${targetPlaylistId}`,
                    songStore.state.botVoiceChannelId || undefined
                ).then(() => {
                    toast.success("Colección enviada al bot");
                }).catch(e => console.error("Error playing in Bot Mode:", e));
            })();
            return;
        }

        songStore.emptyQueue();
        songStore.setCurrentSong(undefined);
        songStore.setQueue(songsToPlay);
        songStore.playNextSong();
        songStore.emptyHistory();
    };

    const handleClickSaveOffline = async () => {
        if (!artist || !artist.songs) {
            return;
        }

        setSaving(true);
        const downloadedSongs: Song[] = [];
        for (const song of artist.songs) {
            let isSongInDB = false;
            try {
                isSongInDB = !!(await getSongFromDB(song.id));
            } catch (error) {
                console.error("Error al obtener la canción de la base de datos:", error);
            }
            if (isSongInDB) {
                downloadedSongs.push(song);
            }
        }

        setSongs(songs.map(s => {
            const isSongInDB = downloadedSongs.find(ds => ds.id === s.id);
            if (isSongInDB) {
                return { ...s, download_status: 'downloaded' } as Song;
            }
            return { ...s, download_status: 'downloading' } as Song;
        }));

        const songsPromises: Promise<Song>[] = artist.songs.map(async song => {
            if (song.download_status === 'downloaded') {
                return { ...song };
            }

            return getSongAudio(song).then(async (blob: Blob | undefined) => {
                if (!blob) {
                    return { ...song, download_status: 'error' };
                }
                saveSongToDB(song, blob);
                setSongs(prev => {
                    const index = prev.findIndex(s => s.id === song.id);
                    if (index !== -1) {
                        prev[index] = { ...song, download_status: 'downloaded' };
                    }
                    return [...prev];
                });
                return { ...song, download_status: 'downloaded' };
            });
        });

        try {
            await Promise.all(songsPromises);
            savePlaylistToDB(artist);
            toast.success("Playlist descargada para offline");
        } catch (error) {
            toast.error("Error al guardar la playlist");
        }

        setSaving(false);
    };

    if (needAuth) {
        return (
            <div className="fade custom-modal modal show" style={{
                position: 'initial',
                opacity: 1,
                paddingBottom: '60px',
                display: 'block',
                height: 'fit-content',
            }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <LoginHome onClose={() => navigate("/")} onFinishLogin={() => setLoading(true)} />
                    </div>
                </div>
            </div>
        );
    }

    if (statusError) {
        return (
            <div className="container">
                <h2 className="text-center mt-5">No se encontró la playlist o álbum</h2>
                <p className="text-center">Puede que haya sido eliminada o no exista.</p>
                <div className="d-flex justify-content-center">
                    <button className="btn btn-primary mt-3" onClick={() => navigate("/")}>
                        Volver al inicio
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container spotip17an-playlist" ref={contentRef}>
            <div className={`spotip17an-playlist__secondary-header ${showSecondaryHeader ? "show" : "hide"}`}>
                <ArtistHeader
                    onClickImage={() => {
                        contentRef.current?.scrollTo({
                            top: 0,
                            behavior: 'smooth'
                        });
                    }}
                    summarySize={55}
                    summaryHeader={true}
                    isOwner={owner}
                    isPlaylist={true}
                    isAlbum={isAlbum}
                    creatorName={albumCreatorName}
                    onClickCreator={isAlbum ? handleNavigateToArtist : undefined}
                    artist={artist}
                    loading={loading}
                    handleClickPlay={handleClickPlay}
                    saving={saving}
                    onEditAlbum={owner && isAlbum ? () => setShowEditAlbumModal(true) : undefined}
                />
            </div>
            <ArtistHeader
                isOwner={owner}
                isPlaylist={true}
                isAlbum={isAlbum}
                creatorName={albumCreatorName}
                onClickCreator={isAlbum ? handleNavigateToArtist : undefined}
                artist={artist}
                loading={loading}
                handleClickPlay={handleClickPlay}
                saving={saving}
                handleClickSaveOffline={handleClickSaveOffline}
                onEditAlbum={owner && isAlbum ? () => setShowEditAlbumModal(true) : undefined}
            />
            <SeparatorLine className="mt-0" />
            <SongList
                showSearch={songs.length > 10}
                showIndex={false}
                songs={songs}
                loadingSongs={loading}
                canScrollToQueue
                extraOptions={owner && artist?.id !== 'favs' && !String(artist?.id).startsWith('favs-') ? [
                    {
                        text: isAlbum ? "Eliminar del álbum" : "Eliminar de la lista",
                        icon: <i className="fa-regular fa-square-minus"></i>,
                        onClick: (song, modalApi) => {
                            if (isAlbum) {
                                removeSongFromAlbum(artist?.id + "", String(song.id)).then((res) => {
                                    modalApi.close();
                                    setArtist(res as any);
                                    setSongs(res.songs ?? []);
                                    toast.success("Canción eliminada del álbum");
                                }).catch(() => {
                                    toast.error("Error al eliminar la canción del álbum");
                                });
                            } else {
                                Spotip17anFunctions.use('removeFromPlaylist').call({
                                    songId: song.id,
                                    playlistId: artist?.id + ""
                                }).then((res) => {
                                    modalApi.close();
                                    setArtist(res);
                                    setSongs(res.songs ?? []);
                                });
                            }
                        }
                    }
                ] : []}
            />
            {isAlbum && artist && artist.release_date && <div className="mb-5" style={{ width: "100%", display: "flex", justifyContent: "center", justifyItems: "center" }}>
                <p style={{ fontSize: "13px", color: "var(--color-gray-light)" }}>Fecha de lanzamiento: {new Date(artist.release_date).toLocaleDateString()}</p>
            </div>}
            {owner && isAlbum && (
                <AlbumEditModal
                    show={showEditAlbumModal}
                    album={artist}
                    onClose={() => setShowEditAlbumModal(false)}
                    onSave={(updatedAlbum) => {
                        setArtist(updatedAlbum as any);
                        setSongs(updatedAlbum.songs ?? []);
                    }}
                    onDelete={() => {
                        navigate('/my-library');
                    }}
                />
            )}
        </div>
    );
};

export default Spotip17anPlaylist;
